import type React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { Partner } from '../services/partnerNetwork.types';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';

type ConditionalTooltipProps = {
    partner: Partner;
    children: React.ReactNode;
};
type PartnershipStatusProps = {
    partner: Partner;
};

const getLabelClassForStatus = (status: string) => {
    if (status === 'connected') {
        return 'label-success';
    }
    if (status === 'expired') {
        return 'label-danger';
    }
    return 'label-default';
};

const ConditionalTooltip = (props: ConditionalTooltipProps) => {
    const { partner, children } = props;
    if (partner.status === 'rescinded') {
        const deletionReason = partner.deletionReason;
        const toolTipName = deletionReason === 'duplicate-entry' ? 'duplicateEntry' : 'manualUserDeletion';
        return (
            <OverlayTrigger
                placement='top'
                overlay={
                    <Tooltip id='tooltip' allowOnTouch>
                        <FormattedMessage id={`intl-msg:network.tooltip.${toolTipName}`} />
                    </Tooltip>
                }
            >
                {children}
            </OverlayTrigger>
        );
    }
    return children;
};

const PartnershipStatus = (props: PartnershipStatusProps) => {
    const intl = useIntl();

    const dateFormatter: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    };

    const displayDate = () => {
        const date = partner.status === 'rescinded' ? partner.deletedAt : partner.connectedAt;
        return date && intl.formatDate(new Date(date), dateFormatter);
    };

    const { partner } = props;
    const labelClass = getLabelClassForStatus(partner.status);
    return (
        <div className='space-x-5'>
            <ConditionalTooltip partner={partner}>
                <span className={`label label-condensed ${labelClass}`}>
                    <FormattedMessage id={`intl-msg:network.status.${partner.status}`} />
                    {partner.status === 'connected' && <span className='uikit-icon icon-ok padding-left-5' />}
                </span>
            </ConditionalTooltip>
            <label>{displayDate()}</label>
        </div>
    );
};

export default PartnershipStatus;
